import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  TrendingDown,
  Users,
  DollarSign,
  Target,
  Clock,
  Award,
  AlertCircle,
  ArrowLeft,
  Calendar,
  RefreshCw
} from 'lucide-react';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList
} from 'recharts';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useToast } from '../../hooks/use-toast';
import { leadService } from '../../services/leadService';

const CRMDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState('30'); // dias

  // Carregar estatísticas
  useEffect(() => {
    loadStats();
  }, [dateFilter]);

  const loadStats = async () => {
    setLoading(true);
    try {
      // Calcular datas
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - parseInt(dateFilter));

      const data = await leadService.getDashboardStats(
        startDate.toISOString(),
        endDate.toISOString()
      );
      
      setStats(data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
      toast({
        title: 'Erro ao carregar estatísticas',
        description: 'Não foi possível carregar os dados do dashboard.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando dashboard...</p>
        </div>
      </div>
    );
  }

  // Preparar dados para gráficos
  const statusData = stats?.status_breakdown ? [
    { name: 'Novo', value: stats.status_breakdown.novo || 0, color: '#3b82f6' },
    { name: 'Em Contato', value: stats.status_breakdown.em_contato || 0, color: '#eab308' },
    { name: 'Qualificado', value: stats.status_breakdown.qualificado || 0, color: '#a855f7' },
    { name: 'Proposta Enviada', value: stats.status_breakdown.proposta_enviada || 0, color: '#f97316' },
    { name: 'Negociação', value: stats.status_breakdown.negociacao || 0, color: '#6366f1' },
    { name: 'Ganho', value: stats.status_breakdown.ganho || 0, color: '#22c55e' },
    { name: 'Perdido', value: stats.status_breakdown.perdido || 0, color: '#ef4444' },
  ].filter(item => item.value > 0) : [];

  // Dados do funil (considerar apenas leads ativos no processo)
  const funnelData = [
    { name: 'Novos Leads', value: stats?.status_breakdown?.novo || 0, fill: '#3b82f6' },
    { name: 'Em Contato', value: stats?.status_breakdown?.em_contato || 0, fill: '#eab308' },
    { name: 'Qualificados', value: stats?.status_breakdown?.qualificado || 0, fill: '#a855f7' },
    { name: 'Proposta Enviada', value: stats?.status_breakdown?.proposta_enviada || 0, fill: '#f97316' },
    { name: 'Negociação', value: stats?.status_breakdown?.negociacao || 0, fill: '#6366f1' },
    { name: 'Ganho', value: stats?.status_breakdown?.ganho || 0, fill: '#22c55e' },
  ].filter(item => item.value > 0);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/portal/leads')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
          
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Dashboard CRM</h1>
            <p className="text-gray-600 mt-1">Análise e indicadores de performance comercial</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-gray-500" />
            <Select value={dateFilter} onValueChange={setDateFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Últimos 7 dias</SelectItem>
                <SelectItem value="15">Últimos 15 dias</SelectItem>
                <SelectItem value="30">Últimos 30 dias</SelectItem>
                <SelectItem value="60">Últimos 60 dias</SelectItem>
                <SelectItem value="90">Últimos 90 dias</SelectItem>
                <SelectItem value="180">Últimos 6 meses</SelectItem>
                <SelectItem value="365">Último ano</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button onClick={loadStats} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Atualizar
          </Button>
        </div>
      </div>

      {/* KPIs Principais */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total de Leads */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <Users className="h-4 w-4" />
              Total de Leads
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">
              {stats?.total_leads || 0}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              no período selecionado
            </p>
          </CardContent>
        </Card>

        {/* Taxa de Conversão */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <Target className="h-4 w-4" />
              Taxa de Conversão
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <div className="text-3xl font-bold text-gray-900">
                {stats?.conversion_rate || 0}%
              </div>
              {(stats?.conversion_rate || 0) >= 20 ? (
                <TrendingUp className="h-5 w-5 text-green-500" />
              ) : (
                <TrendingDown className="h-5 w-5 text-red-500" />
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {stats?.status_breakdown?.ganho || 0} leads convertidos
            </p>
          </CardContent>
        </Card>

        {/* Valor em Pipeline */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Pipeline Value
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">
              {leadService.formatCurrency(stats?.pipeline_value || 0)}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              em negociação
            </p>
          </CardContent>
        </Card>

        {/* Leads Ganhos (Valor) */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <Award className="h-4 w-4" />
              Valor Fechado
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">
              {leadService.formatCurrency(stats?.won_value || 0)}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              em vendas realizadas
            </p>
          </CardContent>
        </Card>
      </div>

      {/* KPIs Secundários */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Tempo Médio de Fechamento */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Tempo Médio de Fechamento
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">
              {stats?.avg_days_to_close || 0} dias
            </div>
            <p className="text-xs text-gray-500 mt-1">
              da criação até conversão
            </p>
          </CardContent>
        </Card>

        {/* Leads Inativos */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              Leads Precisam Atenção
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {stats?.inactive_leads || 0}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              sem contato há mais de 7 dias
            </p>
          </CardContent>
        </Card>

        {/* Leads em Negociação */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Em Negociação
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-indigo-600">
              {stats?.status_breakdown?.negociacao || 0}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              oportunidades quentes
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Funil de Vendas */}
        <Card>
          <CardHeader>
            <CardTitle>Funil de Vendas</CardTitle>
            <CardDescription>Distribuição de leads por etapa do processo</CardDescription>
          </CardHeader>
          <CardContent>
            {funnelData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={funnelData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={120} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#3b82f6">
                    {funnelData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                    <LabelList dataKey="value" position="right" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Sem dados no período selecionado</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Distribuição por Status (Pizza) */}
        <Card>
          <CardHeader>
            <CardTitle>Distribuição de Status</CardTitle>
            <CardDescription>Proporção de leads por status atual</CardDescription>
          </CardHeader>
          <CardContent>
            {statusData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <Target className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Sem dados no período selecionado</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top Performers */}
      {stats?.top_performers && stats.top_performers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="h-5 w-5" />
              Top Performers
            </CardTitle>
            <CardDescription>Colaboradores com mais conversões no período</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {stats.top_performers.map((performer, index) => (
                <div key={performer.user_id} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`
                      w-8 h-8 rounded-full flex items-center justify-center font-bold text-white
                      ${index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-orange-600' : 'bg-blue-500'}
                    `}>
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-medium">{performer.name}</p>
                      <p className="text-sm text-gray-500">{performer.conversions} conversões</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-green-500 h-2 rounded-full"
                        style={{
                          width: `${(performer.conversions / stats.top_performers[0].conversions) * 100}%`
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Alertas e Ações Rápidas */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-orange-200 bg-orange-50">
          <CardHeader>
            <CardTitle className="text-orange-800 flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              Atenção Necessária
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-orange-700 mb-4">
              Você tem <strong>{stats?.inactive_leads || 0} leads</strong> que não recebem 
              contato há mais de 7 dias e podem estar esfriando.
            </p>
            <Button
              onClick={() => navigate('/portal/crm/alertas')}
              variant="outline"
              className="border-orange-300 text-orange-700 hover:bg-orange-100"
            >
              Ver Alertas
            </Button>
          </CardContent>
        </Card>

        <Card className="border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="text-green-800 flex items-center gap-2">
              <Target className="h-5 w-5" />
              Oportunidades Quentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-green-700 mb-4">
              Você tem <strong>{stats?.status_breakdown?.negociacao || 0} leads em negociação</strong> com 
              potencial de <strong>{leadService.formatCurrency(stats?.pipeline_value || 0)}</strong>.
            </p>
            <Button
              onClick={() => navigate('/portal/leads?status=negociacao')}
              variant="outline"
              className="border-green-300 text-green-700 hover:bg-green-100"
            >
              Ver Negociações
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CRMDashboard;
